/**
 * PrecinctStandouts — a compact "where's the action" strip for a region's
 * child precincts.
 *
 * Rendered on LDView and SubregionView, both of which already fetch the
 * full `precincts` array, so this adds ZERO network cost — every value is
 * a reduce over data already in hand.
 *
 * It intentionally surfaces MORE than the "top precinct" line on the
 * homepage. The precinct table on these pages is already sorted by voter
 * count, so "largest" is just its first row; the value here is the
 * superlatives the table buries — the most-engaged precinct (highest avg
 * score, rarely the biggest) and the biggest opportunity (lowest avg
 * score, at the bottom of the table). Each card drills into that precinct.
 */
type PrecinctSummary = {
  precinct_num: string;
  precinct_name: string;
  total_voters: number;
  avg_score: number;
  super_voter_pct: number;
};

type Standout = {
  key: string;
  label: string;
  precinct: PrecinctSummary;
  detail: string;
};

function maxBy(items: PrecinctSummary[], pick: (p: PrecinctSummary) => number) {
  return items.reduce((best, p) => (pick(p) > pick(best) ? p : best));
}
function minBy(items: PrecinctSummary[], pick: (p: PrecinctSummary) => number) {
  return items.reduce((best, p) => (pick(p) < pick(best) ? p : best));
}

export function PrecinctStandouts({
  precincts,
  onDrill,
}: {
  precincts: PrecinctSummary[];
  onDrill: (view: string, id: string) => void;
}) {
  if (!precincts || precincts.length === 0) return null;

  const largest = maxBy(precincts, (p) => p.total_voters);
  const mostEngaged = maxBy(precincts, (p) => p.avg_score);
  const opportunity = minBy(precincts, (p) => p.avg_score);

  const cards: Standout[] = [
    {
      key: 'largest',
      label: 'Largest',
      precinct: largest,
      detail: `${largest.total_voters.toLocaleString()} voters`,
    },
    {
      key: 'engaged',
      label: 'Most engaged',
      precinct: mostEngaged,
      detail: `avg score ${mostEngaged.avg_score.toFixed(1)}`,
    },
  ];

  // Only worth showing the low end when there's a spread — with one or two
  // precincts the "opportunity" would just repeat a card above it.
  if (precincts.length >= 3 && opportunity.precinct_num !== mostEngaged.precinct_num) {
    cards.push({
      key: 'opportunity',
      label: 'Biggest opportunity',
      precinct: opportunity,
      detail: `avg score ${opportunity.avg_score.toFixed(1)}`,
    });
  }

  return (
    <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
      <h2 className="text-sm font-bold text-fg-secondary mb-3">Standout precincts</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => onDrill('precinct', c.precinct.precinct_num)}
            className="group text-left rounded-lg bg-bg-base border border-border-base px-3 py-2 hover:border-accent/50 hover:bg-bg-elevated transition-colors"
            title={`Open ${c.precinct.precinct_name}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">{c.label}</div>
            <div className="mt-0.5 font-semibold text-fg-primary truncate flex items-center gap-1">
              <span className="truncate">{c.precinct.precinct_name}</span>
              <span className="text-fg-muted opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">›</span>
            </div>
            <div className="text-xs text-fg-muted">{c.detail}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
